import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Body,
  Card,
  PageHeader,
  Screen,
  ScreenBody,
  SectionHead,
  Stack,
} from '@leanin/ui';
import type { IconName, Tone } from '@leanin/ui';
import type { components } from '@leanin/api-client';
import { accountKeys } from '@/lib/queries/account';
import { SettingsErrorShell } from '@/components/settings/SettingsErrorShell';
import { DataRequestActionTile } from './-components/DataRequestActionTile';
import { DataRequestTable } from './-components/DataRequestTable';

type DsrKind = components['schemas']['DsrKind'];

type ActionDef = {
  readonly kind: DsrKind;
  readonly title: string;
  readonly description: string;
  readonly icon: IconName;
  readonly tone: Tone;
};

const ACTIONS: readonly ActionDef[] = [
  {
    kind: 'access',
    title: 'Access (export)',
    description:
      'Download a copy of all your data — profile, posts, comments, messages, consent history.',
    icon: 'book',
    tone: 'navy',
  },
  {
    kind: 'rectification',
    title: 'Rectification',
    description: 'Correct inaccurate or incomplete data we hold about you.',
    icon: 'edit',
    tone: 'emerald',
  },
  {
    kind: 'erasure',
    title: 'Erasure',
    description:
      'Permanently delete your account and personal data. Some content may be retained per legal obligations.',
    icon: 'trash',
    tone: 'burgundy',
  },
  {
    kind: 'restriction',
    title: 'Restriction of processing',
    description:
      'Pause processing of your data while we investigate a contestation or correction.',
    icon: 'lock',
    tone: 'yellow',
  },
  {
    kind: 'portability',
    title: 'Portability',
    description: 'Get a machine-readable export to take to another service.',
    icon: 'upload',
    tone: 'neutral',
  },
  {
    kind: 'objection',
    title: 'Objection',
    description: 'Object to specific kinds of processing (e.g. profiling, marketing).',
    icon: 'flag',
    tone: 'orange',
  },
];

export const Route = createFileRoute('/_authed/settings/dsr/')({
  component: SettingsDSRPage,
});

function SettingsDSRPage() {
  const query = useQuery({
    ...accountKeys.dsrRequests(),
    retry: false,
  });
  if (query.isError) {
    return (
      <SettingsErrorShell title="Your data" message="Could not load your data requests." />
    );
  }
  if (query.isLoading) return null;
  return (
    <Screen>
      <ScreenBody gap="2xl">
        <PageHeader
          eyebrow="Settings"
          title="Your data"
          subtitle="Your rights under GDPR & similar laws. We respond to every request within 30 days."
          back={{ label: 'Settings', href: '/settings' }}
        />
        <Stack gap="md">
          <SectionHead title="Make a request" />
          <Body color="mute">
            Pick a request type. Each has a 30-day SLA. You&apos;ll see status updates here, and
            we&apos;ll email you when there&apos;s an action to take.
          </Body>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))',
              // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid layout; no Box prop for grid containers
              gap: 'var(--space-md)',
            }}
          >
            {ACTIONS.map((a) => (
              <DataRequestActionTile
                key={a.kind}
                kind={a.kind}
                title={a.title}
                description={a.description}
                icon={a.icon}
                tone={a.tone}
              />
            ))}
          </div>
          <Body size="sm" color="mute">
            You will be asked to confirm before we file the request.
          </Body>
        </Stack>

        <Stack gap="md">
          <SectionHead title="Active requests" />
          <Card pad="default">
            <Stack gap="md">
              <DataRequestTable />
              <Body size="sm" color="mute">
                You can cancel a request while it&apos;s in <em>received</em> or <em>approved</em>{' '}
                status. Once we begin processing, cancellation isn&apos;t possible.
              </Body>
            </Stack>
          </Card>
        </Stack>
      </ScreenBody>
    </Screen>
  );
}
